import React, { useState, useEffect, useMemo } from 'react'
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarExport,
} from '@mui/x-data-grid'
import {
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  Checkbox,
  Tooltip,
} from '@mui/material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import moment from 'moment-timezone'
import { withFirebase } from '../../components/Fb' // Adjust path as needed
import { paymentText } from '../../components/utils/purchases'
import { endsBeforeStart } from '../../components/utils/memberships'
import { getAlgoliaUsers } from '../../components/Algolia/algolia-utils'

// ';' + utf8WithBom is the house convention - anything else opens as one column in a
// Finnish Excel, and without the BOM the ä/ö in the headers arrive mangled. The CSV header
// row itself is MUI's, built from each column's headerName.
function MembershipListExportToolbar() {
  return (
    <GridToolbarContainer style={{ justifyContent: 'flex-start' }}>
      <GridToolbarFilterButton />
      <GridToolbarExport
        csvOptions={{
          fileName: 'jasenlistaus',
          delimiter: ';',
          utf8WithBom: true,
        }}
        printOptions={{ disableToolbarButton: true }}
      />
    </GridToolbarContainer>
  )
}

// Algolia only ever answers for objectIDs we ask about, so the ids the map is keyed by are the
// purchases' own userids - see the join in the component.
const buildColumns = (phoneByUserId) => [
  { field: 'name', headerName: 'Jäsenyys', flex: 1, filterable: false },
  // First and last name are separate columns rather than one 'Henkilö': it lets the grid sort
  // by surname, filter on either half, and hands the CSV two fields instead of one string that
  // every downstream sheet has to split again. row.user is embedded on the purchase, so all of
  // these are free - no join, no extra fetch. It can be missing, hence the guards.
  {
    field: 'firstname',
    headerName: 'Etunimi',
    flex: 1,
    minWidth: 120,
    valueGetter: (value, row) => (row.user && row.user.firstname) || '',
    filterable: true,
  },
  {
    field: 'lastname',
    headerName: 'Sukunimi',
    flex: 1,
    minWidth: 120,
    valueGetter: (value, row) => (row.user && row.user.lastname) || '',
    filterable: true,
  },
  {
    field: 'email',
    headerName: 'Sähköposti',
    flex: 1,
    minWidth: 200,
    valueGetter: (value, row) => (row.user && row.user.email) || '',
  },
  // Joined from Algolia, NOT read off the purchase: both writers of a membership purchase embed
  // only { id, firstname, lastname, email } (memberships-utils.js _addMembershipPurchase and
  // dispatcher.js userPurchasesListener), so row.user.phone has never existed and a column
  // reading it is blank on every row.
  //
  // Firestore is no help either - customers/{cid}/persons stores `user` as a DocumentReference
  // to users/{uid}, and only 74 of 4422 docs at Padel Hannuksenpelto carry a flat phone, so it
  // would cost one read per member to answer. The profile index is the cheap way round, the same
  // one InvoicingContainer uses to pull saldos.
  {
    field: 'phone',
    headerName: 'Puhelin',
    flex: 1,
    minWidth: 130,
    valueGetter: (value, row) =>
      phoneByUserId[row.userid || (row.user && row.user.id)] || '',
  },
  {
    field: 'payment',
    headerName: 'Maksu',
    flex: 1.4,
    minWidth: 220,
    // In the valueGetter rather than a renderCell so the CSV carries the text too: MUI does
    // not export rendered cells. Most memberships are staff added and have no payment at
    // all, which is what paymentText says in so many words.
    valueGetter: (value, row) => paymentText(row),
  },
  {
    field: 'start',
    headerName: 'Alku',
    flex: 1,
    valueFormatter: (value) => new Date(value).toLocaleDateString(),
  },
  {
    field: 'end',
    headerName: 'Loppu',
    flex: 1,
    valueGetter: (value, row) => {
      if (row.permanent) {
        return 'Pysyvä'
      } else if (row.validuntil) {
        return new Date(row.validuntil).toLocaleDateString()
      } else if (row.end) {
        return new Date(row.end).toLocaleDateString()
      } else {
        return ''
      }
    },
    // The valueGetter above is left alone so the exported cell stays a clean date; the
    // warning is display only. Ranges that end before they start do occur in live data.
    renderCell: (params) => {
      if (!endsBeforeStart(params.row)) return params.value
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {params.value}
          <Tooltip title='Päättymispäivä on ennen alkamispäivää'>
            <WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main', ml: 0.5 }} />
          </Tooltip>
        </Box>
      )
    },
  },
  {
    field: 'status',
    headerName: 'Tila',
    flex: 1,
    valueGetter: (value, row) => {
      if (row.permanent) {
        return 'Aktiivinen'
      } else if (
        row.validuntil &&
        moment(row.validuntil).isBefore(moment())
      ) {
        return 'Päättynyt'
      } else if (row.end && moment(row.end).isBefore(moment())) {
        return 'Päättynyt'
      } else {
        return 'Aktiivinen'
      }
    },
  },
  // {
  //   field: 'edit',
  //   headerName: 'Edit',
  //   flex: 1,
  //   disableClickEventBubbling: true,
  //   renderCell: (params) => (
  //     <EditButton
  //       loading={params.api.getRow(params.row.id).loading}
  //       onClick={() => params.api.getRow(params.row.id).onEdit(params.row.id)}
  //     />
  //   ),
  // },
]

const MembershipsListsContainer = ({ firebase }) => {
  const [membershipsData, setMembershipsData] = useState([])
  const [filterMembershipIds, setFilterMembershipIds] = useState([])
  const [filteredMemberships, setFilteredMemberships] = useState([])
  const [cMemberships, setCMemberships] = useState([])
  const [phoneByUserId, setPhoneByUserId] = useState({})

  // Rebuilt only when the phone map arrives. A fresh array on every render would make the grid
  // drop the admin's column widths and sorting mid-use.
  const columns = useMemo(() => buildColumns(phoneByUserId), [phoneByUserId])

  const handleFilterChange = (event) => {
    const {
      target: { value },
    } = event
    const selectedIds = value.map((v) =>
      cMemberships.find((mem) => mem.value === v)
    )
    setFilterMembershipIds(selectedIds)
  }

  useEffect(() => {
    Promise.all([
      firebase.getPurchasedMemberships(), // Adjust method as needed
      firebase.getMemberships(),
    ])
      .then(([memberships, customerMemberships]) => {
        let _customerMemberships = []
        if (!customerMemberships.empty) {
          _customerMemberships = customerMemberships.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
        }
        if (!memberships.empty) {
          memberships = memberships.map((doc) => ({
            ...doc,
            id: doc.id,
            name:
              _customerMemberships.find((cm) => cm.id === doc.membershipid)
                ?.name || 'N/A',
          }))
        } else {
          memberships = []
        }
        setMembershipsData(memberships)
        setFilteredMemberships(memberships)

        if (!customerMemberships.empty) {
          customerMemberships = customerMemberships.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
        }
        setCMemberships([
          { label: 'Kaikki', value: 'Kaikki' },
          ...Array.from(
            new Set(
              customerMemberships.map((mem) => {
                return { label: mem.name, value: mem.id }
              })
            )
          ),
        ])
      })
      .catch((error) => {
        console.error('Error fetching benefit cards:', error)
      })
  }, [firebase])

  // The Puhelin column. Asks the profile index for one attribute on the members actually listed,
  // which is a single request instead of a Firestore read per member - the same trick
  // InvoicingContainer uses for saldos.
  //
  // Chunked because getObjects sends every id in one request and Algolia caps that at 1000; a
  // venue with more members than that would otherwise get nothing at all. Deduped first, since
  // one person holding several memberships is the normal case here.
  //
  // Failing is not fatal - the column stays empty and the rest of the listing still works - so
  // this is its own chain rather than another leg of the Promise.all above.
  useEffect(() => {
    const ids = Array.from(
      new Set(
        membershipsData
          .map((m) => m.userid || (m.user && m.user.id))
          .filter(Boolean)
      )
    )
    if (ids.length === 0) return undefined
    let cancelled = false
    const chunks = []
    for (let i = 0; i < ids.length; i += 1000) chunks.push(ids.slice(i, i + 1000))
    Promise.all(chunks.map((chunk) => getAlgoliaUsers(chunk, ['phone'])))
      .then((responses) => {
        const map = {}
        responses.forEach((response) => {
          ;((response && response.results) || []).forEach((r) => {
            // A member missing from the index comes back as null, not as an error.
            if (r && r.objectID && r.phone) map[r.objectID] = r.phone
          })
        })
        if (!cancelled) setPhoneByUserId(map)
      })
      .catch((error) => {
        console.error('Error fetching phone numbers from Algolia:', error)
      })
    return () => {
      cancelled = true
    }
  }, [membershipsData])

  useEffect(() => {
    // 'Kaikki' is an option this view offers itself, and it is not a membershipid - without
    // this case it matches no row and empties the grid, which the export then writes out.
    if (
      filterMembershipIds.length === 0 ||
      filterMembershipIds.some((fMem) => fMem && fMem.value === 'Kaikki')
    ) {
      setFilteredMemberships(membershipsData)
    } else {
      setFilteredMemberships(
        membershipsData.filter((mem) =>
          filterMembershipIds.some((fMem) => fMem.value === mem.membershipid)
        )
      )
    }
  }, [filterMembershipIds, membershipsData])

  return (
    <div className='content' style={{ padding: 20, paddingTop: 10 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <FormControl size='small' sx={{ minWidth: 200 }}>
            <InputLabel id='benefit-type-filter-label'>
              Näytä jäsenyydet
            </InputLabel>
            <Select
              labelId='benefit-type-filter-label'
              value={filterMembershipIds.map((mem) => mem.value)}
              multiple
              label='Näytä jäsenyydet'
              onChange={handleFilterChange}
              renderValue={(selected) => {
                return selected
                  .map((s) => cMemberships.find((c) => c.value === s))
                  .map((d) => d.label)
                  .join(', ')
              }}
            >
              {cMemberships.map((mem) => {
                const isChecked = filterMembershipIds.some(
                  (fMem) => fMem.value === mem.value
                )
                return (
                  <MenuItem key={mem.value} value={mem.value}>
                    <Checkbox checked={isChecked} />
                    {mem.label}
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ height: '100%', width: '100%' }}>
          <div style={{ display: 'flex', height: '100%' }}>
            <div style={{ flexGrow: 1, minWidth: 0 }}>
              {/* DataGrid is v8: components/componentsProps were renamed slots/slotProps in
                  v6 and removed in v7, and v8 also hides the toolbar unless showToolbar is
                  set - which is why the export button was never there. pageSize went the
                  same way and is now initialState + pageSizeOptions. */}
              <DataGrid
                autoHeight
                density='compact'
                rows={filteredMemberships}
                columns={columns}
                showToolbar
                slots={{ toolbar: MembershipListExportToolbar }}
                initialState={{
                  pagination: { paginationModel: { pageSize: 20 } },
                }}
                pageSizeOptions={[20, 50, 100]}
                pagination
                disableRowSelectionOnClick
              />
            </div>
          </div>
        </Box>
      </Paper>
    </div>
  )
}

export default withFirebase(MembershipsListsContainer)
